import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { TimePickerComponent } from '../../time-picker/time-picker.component';
import { updateTime } from '../../helper/date-format.helper';
import { addMinutes } from 'date-fns';

@Component({
  selector: 'app-reschedule-modal',
  standalone: true,
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, TimePickerComponent],
  template: `
    <h2 mat-dialog-title>Reschedule Appointment</h2>
    <mat-dialog-content>
      <form [formGroup]="rescheduleForm">
        <div class="row">
          <!-- Date Field -->
          <div class="col-12 mb-3">
            <mat-form-field appearance="outline" class="w-100"> 
              <mat-label>New Date</mat-label>
              <input matInput [matDatepicker]="picker" formControlName="startDate" 
                     [min]="minDate" required />
              <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker [startAt]="minDate"></mat-datepicker>
              <mat-error *ngIf="rescheduleForm.get('startDate')?.hasError('required')">
                Date is required
              </mat-error>
              <mat-error *ngIf="rescheduleForm.get('startDate')?.hasError('pastDate')">
                Cannot reschedule to a past date
              </mat-error>
            </mat-form-field>
          </div>
          
          <!-- Start Time Field -->
          <div class="col-md-6">
            <app-time-picker
              label="Start Time"
              [control]="startTimeControl"
              [focusTime]="focusTime"
            ></app-time-picker>
            <mat-error *ngIf="startTimeControl.touched && startTimeControl.invalid" class="time-error">
              {{ getTimeErrorMessage('startTime') }}
            </mat-error>
          </div>
      
          <!-- End Time Field -->
          <div class="col-md-6">
            <app-time-picker
              label="End Time"
              [control]="endTimeControl"
              [focusTime]="endFocusTime"
            ></app-time-picker>
            <mat-error *ngIf="endTimeControl.touched && endTimeControl.invalid" class="time-error">
              {{ getTimeErrorMessage('endTime') }}
            </mat-error>
          </div>
          
          <div class="col-12" *ngIf="rescheduleForm.hasError('endTimeInvalid')">
            <mat-error class="time-error">
              {{ rescheduleForm.getError('endTimeInvalid').message }}
            </mat-error>
          </div>

          <!-- Past time warning -->
          <div class="col-12" *ngIf="rescheduleForm.hasError('pastDateTime')">
            <mat-error class="time-error">
              {{ rescheduleForm.getError('pastDateTime').message }}
            </mat-error>
          </div>
        </div>
        
        <!-- Reschedule Reason (Optional) -->
        <mat-form-field appearance="outline" class="w-100 mt-3">
          <mat-label>Reason for Rescheduling (Optional)</mat-label>
          <textarea matInput formControlName="reason" rows="3"></textarea>
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="onReschedule()" [disabled]="!rescheduleForm.valid">
        Confirm Reschedule
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      padding: 20px;
      min-width: 500px;
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      margin: 10px -5px;
    }
    .col-12 {
      width: 100%;
      padding: 0 5px;
    }
    .col-md-6 {
      flex: 1;
      padding: 0 5px;
      min-width: 45%;
    }
    .mb-3 {
      margin-bottom: 1rem;
    }
    .w-100 {
      width: 100%;
    }
    .time-error {
      color: #f44336;
      font-size: 12px;
      margin-top: -16px;
      margin-bottom: 12px;
      display: block;
    }
    ::ng-deep .mat-form-field-flex {
      padding: 0 !important;
    }
    ::ng-deep .mat-form-field-infix {
      padding: 0.5em 0 !important;
    }
  `]
})
export class RescheduleModalComponent {
  rescheduleForm: FormGroup;
  focusTime: string;
  endFocusTime: string;
  minDate: Date;

  get startTimeControl(): FormControl {
    return this.rescheduleForm.get('startTime') as FormControl;
  }

  get endTimeControl(): FormControl {
    return this.rescheduleForm.get('endTime') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RescheduleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Set minimum date to today
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);

    // Set default times (round to next 30-minute interval)
    const now = new Date();
    const roundedTime = this.roundToNext30Minutes(now);
    const endTime = addMinutes(roundedTime, 30);
    
    this.focusTime = this.formatTime(roundedTime);
    this.endFocusTime = this.formatTime(endTime);

    // Initialize form with validation
    this.rescheduleForm = this.fb.group({
      startDate: [this.minDate, [Validators.required, this.pastDateValidator.bind(this)]],
      startTime: [this.focusTime, [Validators.required, this.timeFormatValidator.bind(this)]],
      endTime: [this.endFocusTime, [Validators.required, this.timeFormatValidator.bind(this)]],
      reason: ['']
    });

    // Auto-update end time when start time changes and validate constraints
    this.rescheduleForm.get('startTime')?.valueChanges.subscribe(startTime => {
      if (startTime && this.isValidTimeFormat(startTime)) {
        // Parse the time to a date object for calculation
        const startHour = this.parseTimeToHours(startTime);
        const endHour = startHour + 0.5; // Add 30 minutes
        const endTimeString = this.formatTimeFromHours(endHour);
        
        // Update end time if it's invalid or less than 30 minutes after start time
        const currentEndTime = this.rescheduleForm.get('endTime')?.value;
        if (!currentEndTime || this.isEndTimeInvalid(startTime, currentEndTime)) {
          this.rescheduleForm.patchValue({ endTime: endTimeString }, { emitEvent: false });
        }
      }
      
      // Revalidate the form when time changes
      this.rescheduleForm.updateValueAndValidity();
    });

    // Validate when date changes
    this.rescheduleForm.get('startDate')?.valueChanges.subscribe(() => {
      // Revalidate time fields when date changes
      this.rescheduleForm.get('startTime')?.updateValueAndValidity();
      this.rescheduleForm.get('endTime')?.updateValueAndValidity();
      this.rescheduleForm.updateValueAndValidity();
    });

    // Add custom validators to the form
    this.rescheduleForm.setValidators([
      this.endTimeValidator.bind(this),
      this.pastDateTimeValidator.bind(this)
    ]);
  }

  // Format time as hh:mm AM/PM (same format as TimePickerComponent)
  private formatTime(time: Date): string {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  }

  // Parse time from AM/PM format to hours (decimal)
  private parseTimeToHours(timeStr: string): number {
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let hour24 = hours;
    
    if (period === 'PM' && hours !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0;
    }
    
    return hour24 + minutes / 60;
  }

  // Format hours (decimal) back to time string
  private formatTimeFromHours(hours: number): string {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    
    const date = new Date();
    date.setHours(h, m, 0, 0);
    return this.formatTime(date);
  }

  private roundToNext30Minutes(date: Date): Date {
    const minutes = date.getMinutes();
    const roundedMinutes = minutes < 30 ? 30 : 0;
    const roundedDate = new Date(date);
    roundedDate.setMinutes(roundedMinutes);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);
    
    if (minutes >= 30) {
      roundedDate.setHours(roundedDate.getHours() + 1);
    }
    
    return roundedDate;
  }

  private isValidTimeFormat(time: string): boolean {
    // Check if time is in the format "h:mm AM/PM"
    return !!time.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
  }

  // Validator to prevent past dates
  private pastDateValidator(control: FormControl): { [key: string]: any } | null {
    if (!control.value) return null;
    
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return { pastDate: { value: control.value, message: 'Cannot reschedule to a past date' } };
    }
    
    return null;
  }

  private timeFormatValidator(control: FormControl): { [key: string]: any } | null {
    if (!control.value) return null;
    
    if (!this.isValidTimeFormat(control.value)) {
      return { timeFormat: { value: control.value, message: 'Time must be in format: HH:MM AM/PM' } };
    }
    
    return null;
  }

  private endTimeValidator(formGroup: FormGroup): { [key: string]: any } | null {
    const startTime = formGroup.get('startTime')?.value;
    const endTime = formGroup.get('endTime')?.value;
    
    if (!startTime || !endTime) return null;
    
    if (this.isEndTimeInvalid(startTime, endTime)) {
      return { endTimeInvalid: { message: 'End time must be at least 30 minutes after start time' } };
    }
    
    return null;
  }

  // New validator to prevent scheduling in the past (considering both date and time)
  private pastDateTimeValidator(formGroup: FormGroup): { [key: string]: any } | null {
    const startDate = formGroup.get('startDate')?.value;
    const startTime = formGroup.get('startTime')?.value;
    
    if (!startDate || !startTime) return null;
    
    const selectedDateTime = this.combineDateAndTime(startDate, startTime);
    const now = new Date();
    
    if (selectedDateTime <= now) {
      return { pastDateTime: { message: 'Cannot reschedule to a past date and time' } };
    }
    
    return null;
  }

  // Helper method to combine date and time
  private combineDateAndTime(date: Date, timeString: string): Date {
    const timeMatch = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!timeMatch) return new Date(date);
    
    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const period = timeMatch[3].toUpperCase();
    
    if (period === 'PM' && hours < 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    const combinedDateTime = new Date(date);
    combinedDateTime.setHours(hours, minutes, 0, 0);
    
    return combinedDateTime;
  }

  private isEndTimeInvalid(startTime: string, endTime: string): boolean {
    const startHours = this.parseTimeToHours(startTime);
    const endHours = this.parseTimeToHours(endTime);
    
    // End time must be at least 30 minutes (0.5 hours) after start time
    return endHours < startHours + 0.5;
  }

  // Get appropriate error message for time fields
  getTimeErrorMessage(fieldName: string): string {
    const control = this.rescheduleForm.get(fieldName);
    if (!control) return '';
    
    if (control.hasError('required')) {
      return 'Time is required';
    }
    
    if (control.hasError('timeFormat')) {
      return control.getError('timeFormat').message || 'Invalid time format';
    }
    
    return 'Invalid time';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onReschedule(): void {
    if (this.rescheduleForm.valid) {
      const formValue = this.rescheduleForm.value;
      
      // Double-check that we're not scheduling in the past
      const selectedDateTime = this.combineDateAndTime(formValue.startDate, formValue.startTime);
      const now = new Date();
      
      if (selectedDateTime <= now) {
        // This should not happen due to validators, but as a safety check
        console.error('Attempted to reschedule to past date/time');
        return;
      }
      this.dialogRef.close({
        startDate: updateTime(formValue.startDate, formValue.startTime),
        endDate: updateTime(formValue.startDate, formValue.endTime),
        reason: formValue.reason || 'Appointment rescheduled'
      });
    }
  }
}